<template>
  <div class="settings-access">
    <div v-if="$slots.legendCaption" class="settings-access__title">
      <slot name="legendCaption" />
    </div>
    <div :class="{'settings-access__content': $slots.legendCaption}">
      <!-- ФОРМА ДОБАВЛЕНИЯ БАНОВ (показывается когда modeAddBan = true) -->
      <template v-if="modeAddBan">
        <div v-for="(itemField, indexField) in formAddBan" :key="`fieldAddBan${indexField}`" class="input-row settings-access__input-row">
          <SmartVSelect
            v-model="itemField.employeeId"
            :settings-remote-search="settingsSelectEmployees"
            class="settings-access__select"
            :placeholder="$t('selectServicePlaceholder')"
          />

          <div class="buttons-group">
            <SmartInputText
              v-model="itemField.reason"
              :placeholder="$t('reasonPlaceholder')"
              type="text"
              class="settings-access__select"
            />
            <CamsButton
              v-if="lengthFormAddBan > 1"
              class="settings-access__button-delete-field"
              icon-type="only"
              type="button"
              @click="deleteFieldFormAddBan(indexField)"
            >
              <svg class="icon-close">
                <use xlink:href="#close" />
              </svg>
            </CamsButton>
          </div>
        </div>

        <div class="buttons-group input-row">
          <CamsButton
            :disabled="lengthFormAddBan >= 3"
            priority="primary"
            @click="addNewFieldFormAddBan()"
          >
            {{ $t('more') }}
          </CamsButton>
          <CamsButton
            :disabled="!isFormValid || isLoading"
            priority="primary"
            type="button"
            @click="addBans()"
          >
            {{ $t('save') }}
          </CamsButton>
          <CamsButton type="button" @click="switchOffModeAddBan()">
            {{ $t('cancel') }}
          </CamsButton>
        </div>
      </template>

      <!-- КНОПКА ДОБАВЛЕНИЯ (показывается когда modeAddBan = false) -->
      <div v-else class="input-row">
        <CamsButton
          priority="primary"
          type="button"
          @click="switchOnModeAddBan()"
        >
          {{ $t('addBan') }}
        </CamsButton>
      </div>

      <!-- СПИСОК БАНОВ (показывается всегда) -->
      <div class="input-row">
        <SpinnerLoading v-if="isLoading" size="s" />

        <div v-else class="access-container">
          <!-- ПОИСК (показывается всегда) -->
          <div class="input-row">
            <SmartInputText
              v-model="search"
              :placeholder="$t('employeeFilter')"
              type="text"
              @input="debouncedLoadBans()"
            />
          </div>

          <!-- СПИСОК БАНОВ -->
          <template v-if="isAvailableBans">
            <div class="access-container__header">
              <div>Пользователь</div>
              <div>Дата блокировки</div>
              <div>Причина</div>
            </div>

            <div class="access-container__content">
              <div v-for="(banInfo, banInfoIndex) in bansInfo" :key="banInfoIndex" class="access-container__list-item">
                <div>{{ banInfo.employeeTitle }}</div>
                <div>{{ banInfo.date | localDateTime }}</div>
                <div>{{ banInfo.reason }}</div>

                <div class="buttons-group">
                  <CamsButton
                    class="settings-access__button-delete-field"
                    icon-type="only"
                    type="button"
                    @click="deleteBan(banInfo.employeeId, banInfo.deviceId)"
                  >
                    <svg class="icon-close">
                      <use xlink:href="#close" />
                    </svg>
                  </CamsButton>
                </div>
              </div>
            </div>

            <div>
              <nav v-if="pageInfo.numPages > 1" class="pagination-small">
                <paginate
                  v-model="currentPage"
                  :active-class="'pagination-small__list-item--active'"
                  :break-view-class="'pagination-small__list-item--collapse'"
                  :click-handler="loadBans"
                  :container-class="'pagination-small__list'"
                  :hide-prev-next="true"
                  :page-class="'pagination-small__list-item'"
                  :page-count="pageInfo.numPages"
                  :page-range="5"
                  next-text=""
                  prev-text=""
                />
              </nav>
              <div class="pagination-small__count">
                {{ $t('foundCount') }} {{ pageInfo.count }}
              </div>
            </div>
          </template>

          <!-- СООБЩЕНИЕ КОГДА БАНОВ НЕТ -->
          <div v-else-if="!modeAddBan">
            <p>{{ $t('noBans') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ACTION_ADD_EMPLOYEES_BANS,
  ACTION_REMOVE_EMPLOYEES_BANS,
  ACTION_LOAD_EMPLOYEE_BANS_FOR_EDIT,
  EMPLOYEE_BAN_STRUCT,
} from "@/store/pacs/devices/index.js";
import {ACTION_LOAD_EMPLOYEES_FOR_SELECT, EMPLOYEE_STRUCT} from "@/store/pacs/employees/index.js";
import {setBansMixin, setPermissionsMixin} from "@/components/pacs/pacsPermissions/mixin.js";

/**
 * Компонент диалога управления банами сотрудников.
 *
 * Предназначен для добавления и удаления сотрудников из черного списка услуг.
 */
export default {
  mixins: [setPermissionsMixin,setBansMixin],

  props: {
    /**
     * Идентификатор сотрудника - позволяет управлять банами для этого сотрудника.
     */
    employeeId: {
      type: Number,
    },
    deviceId: {
      type: Number,
      required: true
    },
  },
  data() {
    return {
      settingsSelectEmployees: {
        action: `pacs/employees/${ACTION_LOAD_EMPLOYEES_FOR_SELECT}`,
        valueField: EMPLOYEE_STRUCT.fields.id.name,
        labelField: EMPLOYEE_STRUCT.fields.title.name
      },
    };
  },
  created() {
    this.debouncedLoadBans = _.debounce(this.loadBans, 350);
    this.loadBans();
  },
  computed: {
    /**
     * Форма валидна, когда в КАЖДОЙ строке выбран сотрудник и заполнена причина.
     */
    isFormValid() {
      if (!this.modeAddBan) return false;
      const rows = Object.values(this.formAddBan || {});
      if (!rows.length) return false;

      return rows.every((row) => {
        const id = this.normalizeEmployeeId(row.employeeId);
        return Number.isFinite(id) && id > 0;
      });
    },
  },
  methods: {
    normalizeEmployeeId(raw) {
      let id = raw;
      if (id && typeof id === "object") {
        id = id.id ??
          id[EMPLOYEE_STRUCT.fields.id.name] ??
          id.value ??
          null;
      }
      id = id != null ? Number(id) : null;
      return id;
    },
    /**
     * Загрузит информацию по банам для редактирования.
     *
     * @param {Number} currentPage
     */
    async loadBans(currentPage = 1) {
      this.currentPage = this.currentPage === currentPage ? this.currentPage : currentPage;
      this.isLoading = true;
      try {
        const responseData = await this.$store.dispatch(`pacs/devices/${ACTION_LOAD_EMPLOYEE_BANS_FOR_EDIT}`, {
          deviceId: this.deviceId,
          search: this.search || "",
          page: currentPage
        });

        this.pageInfo.count = responseData.count;
        this.pageInfo.numPages = responseData.page.all;
        this.bansInfo = responseData.results.map((rawBanInfo) => {
          return {
            employeeId: rawBanInfo[EMPLOYEE_BAN_STRUCT.fields.employee_id.name],
            deviceId: rawBanInfo[EMPLOYEE_BAN_STRUCT.fields.device_id.name],
            reason: rawBanInfo[EMPLOYEE_BAN_STRUCT.fields.reason.name],
            deviceTitle: rawBanInfo[EMPLOYEE_BAN_STRUCT.fields.device_title.name],
            employeeTitle: rawBanInfo[EMPLOYEE_BAN_STRUCT.fields.employee_title.name],
            date: rawBanInfo[EMPLOYEE_BAN_STRUCT.fields.date.name],
          };
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Обработка формы для добавления банов и оформление запроса по заполненным данным.
     */
    async addBans() {
      if (!this.isFormValid) return;
      const rows = Object.values(this.formAddBan)
        .map((row) => ({
          employeeId: this.normalizeEmployeeId(row.employeeId),
          reason: (row.reason || "").trim(),
        }));

      this.isLoading = true;
      try {
        for (const r of rows) {
          await this.$store.dispatch(`pacs/devices/${ACTION_ADD_EMPLOYEES_BANS}`, {
            employeeId: r.employeeId,
            deviceId: this.deviceId,
            reason: r.reason
          });
        }
        this.switchOffModeAddBan();
        this.$notify({
          group: "main",
          text: this.$t("bansAdded"),
          duration: 5000,
          type: "success",
        });
        await this.loadBans();
      } catch (e) {
        let errorMessage = this.$t('errorAddingBan');
        if (e?.fields?.non_field_errors && e.fields.non_field_errors.length > 0) {
          errorMessage = e.fields.non_field_errors[0];
        } else if (e?.message) {
          errorMessage = e.message;
        } else if (typeof e === 'string') {
          errorMessage = e;
        }
        this.$camsdals.alert(errorMessage);
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Удаление бана из списка.
     *
     * @param {Number} employeeId
     * @param {Number} deviceId
     */
    deleteBan(employeeId, deviceId) {
      this.$camsdals.confirm(this.$t('confirmDeleteBan'), async () => {
        this.isLoading = true;
        try {
          await this.$store.dispatch(`pacs/devices/${ACTION_REMOVE_EMPLOYEES_BANS}`, {
            employeeId,
            deviceId
          });
          this.$notify({
            group: "main",
            text: this.$t('banDeleted'),
            duration: 5000,
            type: "success",
          });
        } catch {
          this.$camsdals.alert(this.$t('errorDeletingBan'));
        }
        this.loadBans();
        this.isLoading = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.access-container {
  // общий отступ между блоками
  &__header,
  &__list-item {
    display: grid;
    align-items: center;
    column-gap: 12px;
    // 4 колонки: Пользователь | Дата | Причина | кнопка
    // подбери числа под ширину модалки, но так уже ровно смотрится
    grid-template-columns:
      minmax(220px, 1.2fr)
      180px
      minmax(260px, 1fr)
      6px;
  }

  &__header {
    font-weight: 600;
    padding: 8px 12px;
    background: rgba(0,0,0,0.04);
    border-bottom: 1px solid rgba(0,0,0,0.08);
    position: sticky;
    top: 0;
    z-index: 1;
  }

  &__content {
    word-break: break-word;
  }
  &__list-item {
    padding: 10px 12px;
    border-bottom: 1px solid rgba(0,0,0,0.06);
    &:nth-child(odd) {
      background: rgba(0,0,0,0.02);
    }
    // 3-я колонка = Причина: перенос и нормальная высота
    > div:nth-child(3) {
      white-space: normal;       // разрешаем многострочность
      overflow-wrap: anywhere;   // переносим «склеенные» длинные слова
    }

    // 4-я колонка (кнопка «крестик») — прижать вправо
    .buttons-group {
      justify-self: end;
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
  // на узких экранах — одна колонка в столбик (опционально)
  @media (max-width: 720px) {
    &__header,
    &__list-item {
      grid-template-columns: 1fr;
      row-gap: 6px;
    }
    &__header {
      position: static;
    }
  }
}
.icon-close {
  width: 18px;
  height: 18px;
  display: block;
}

</style>
